const ART_FRAME_BORDER = 1

/**
 * The museum matte: a single value applied equally on all four sides of
 * .gallery-art-frame. Mirrors the CSS `--gallery-matte: clamp(12px, 2vw, 24px)`
 * so JS sizing and the rendered frame agree to the pixel. Scales with the
 * viewport but is always equal on every side.
 */
export const getArtMatte = (windowWidth: number) =>
  Math.round(Math.min(24, Math.max(12, (windowWidth > 0 ? windowWidth : 1280) * 0.02)))

/** Total horizontal/vertical space the frame adds around the art (2× matte + 2× border). */
export const getArtworkFramePadding = (windowWidth: number) =>
  getArtMatte(windowWidth) * 2 + ART_FRAME_BORDER * 2

/** Inner width of the centered gallery container (max 1200px) minus its side padding. */
export const getGalleryContainerInnerWidth = (windowWidth: number) => {
  const sidePadding = windowWidth >= 768 ? 32 : windowWidth >= 640 ? 24 : 20 // px-8 / px-6 / px-5
  return Math.max(150, Math.min(windowWidth, 1200) - sidePadding * 2)
}

/** At/above this width the piece detail page is a two-column museum catalog. */
export const DETAIL_TWO_COLUMN_MIN_WIDTH = 1024

/**
 * Width of the art column when the detail page is two-column (art ~1.6fr,
 * metadata ~0.95fr with a 20rem floor). Mirrors the CSS grid so the art fits
 * its column exactly. Returns the full container width when single-column.
 */
export const getDetailArtColumnWidth = (windowWidth: number) => {
  const containerInner = getGalleryContainerInnerWidth(windowWidth)
  if (windowWidth < DETAIL_TWO_COLUMN_MIN_WIDTH) return containerInner
  const gap = windowWidth >= 1280 ? 56 : 40 // xl:gap-14 / lg:gap-10
  const free = containerInner - gap
  const metadataMin = 320 // 20rem floor
  let artColumn = free * (1.6 / 2.55)
  if (free - artColumn < metadataMin) artColumn = free - metadataMin
  return Math.max(150, Math.floor(artColumn) - 8) // small safety vs sub-pixel fr rounding
}

export interface ArtStageSize {
  /** Rendered artwork width in px (already contain-fit to the stage). */
  width: number
  /** Rendered artwork height in px. */
  height: number
  /** Usable stage height (viewport minus header + breadcrumb + controls). */
  stageHeight: number
}

const HEADER_HEIGHT = 64

/**
 * Size an artwork to *fit within the viewport* on the detail/collection hero —
 * height-led (so portrait pieces fill the vertical), falling back to width-led
 * for wide pieces (contain). Subtracts the real page chrome (sticky header,
 * breadcrumb, controls bar, breathing room) so the full piece + its controls
 * clear the fold, and lets the stage grow with the viewport instead of being
 * frozen by the 1200px content cap.
 */
export const getArtStageSize = (
  viewportWidth: number,
  viewportHeight: number,
  aspectRatio: number
): ArtStageSize => {
  const ar = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1
  const vw = viewportWidth > 0 ? viewportWidth : 1280
  const vh = viewportHeight > 0 ? viewportHeight : 800

  const isShort = vh < 560
  const frameChrome = getArtworkFramePadding(vw)

  // Vertical chrome below the sticky header: page padding + breadcrumb +
  // controls bar + breathing room (the frame's own matte is added separately
  // via frameChrome) so the piece *and* its controls clear the fold.
  const base = isShort ? 110 : vw >= 768 ? 186 : 194
  const availHeight = Math.max(150, vh - HEADER_HEIGHT - base - frameChrome)
  // Size the art so the frame (art + equal matte + border) fits its column
  // exactly — never overflowing, so the matte stays equal on all four sides.
  // At ≥1024px the art lives in the left column of the two-column catalog, so
  // it fits that column; below that it spans the full container.
  const availWidth = Math.max(150, getDetailArtColumnWidth(vw) - frameChrome)

  let height = availHeight
  let width = height * ar
  if (width > availWidth) {
    width = availWidth
    height = width / ar
  }

  return { width: Math.floor(width), height: Math.floor(height), stageHeight: availHeight }
}

export const getCollectionGridTokenWidth = (windowWidth: number) => {
  const containerWidth = Math.min(windowWidth, 1200)
  const horizontalPadding = windowWidth >= 768 ? 64 : windowWidth >= 640 ? 48 : 40
  const filterColumnWidth = windowWidth >= 1024 ? 256 : 0
  const filterGridGap = windowWidth >= 1024 ? 32 : 0
  const gridGap = windowWidth >= 768 ? 24 : 20
  const columns = windowWidth >= 1024 ? 4 : windowWidth >= 768 ? 3 : windowWidth >= 640 ? 2 : 1
  const availableWidth = containerWidth - horizontalPadding - filterColumnWidth - filterGridGap

  return Math.floor(Math.max(140, (availableWidth - gridGap * (columns - 1)) / columns - 32))
}
